import React, { useMemo, useState } from "react";
import { Icon } from "@iconify/react";
import { toast } from "react-toastify";
import { useLocation } from "react-router-dom";
import { ButtonPrimary } from "../../components/utils";
import { FileDropzone } from "../../components/shared/FileDropzone";
import { Instructions } from "../../components/shared/Instructions";
import { SearchableSelect } from "../../components/shared/SearchableSelect";
import convertToBase64 from "../../utils/convertToBase64";
import {
  useDownloadGrantIdTemplate,
  useParseGrantIdUpdateUpload,
} from "../../queries/bulkGrantUpdate";
import { useGrants } from "../../queries/esopPlan";
import {
  GrantIdUpdateRow,
  SelectedGrantSeed,
} from "../../types/BulkGrantUpdate";
import BulkUpdateGrantIdsPreview from "./BulkUpdateGrantIdsPreview";
import { Input } from "../../components/shared/InputField";

type EntryMode = "manual" | "upload";

type RouteState = {
  selectedGrants?: SelectedGrantSeed[];
};

const BulkUpdateGrantIds: React.FC = () => {
  const location = useLocation();
  const routeState = (location.state || {}) as RouteState;
  const selectedGrantSeeds = routeState.selectedGrants || [];

  const seedRows = useMemo<GrantIdUpdateRow[]>(
    () =>
      selectedGrantSeeds.map((grant, index) => ({
        oldGrantId: grant.grantIdentifier,
        newGrantId: "",
        rowIndex: index,
      })),
    [selectedGrantSeeds]
  );

  const [entryMode, setEntryMode] = useState<EntryMode>(
    selectedGrantSeeds.length > 0 ? "manual" : "upload"
  );
  const [file, setFile] = useState<File>();
  const [errors, setErrors] = useState<string[]>([]);
  const [parsedRows, setParsedRows] = useState<GrantIdUpdateRow[]>([]);
  const [manualRows, setManualRows] = useState<GrantIdUpdateRow[]>(
    seedRows.length > 0
      ? seedRows
      : [{ oldGrantId: "", newGrantId: "", rowIndex: 0 }]
  );
  const [previewRows, setPreviewRows] = useState<GrantIdUpdateRow[]>([]);
  const [showPreview, setShowPreview] = useState(false);

  const { data: grantsData } = useGrants();
  const grantIdentifiers = useMemo(
    () => grantsData?.grants.map((g) => g.grantIdentifier) ?? [],
    [grantsData]
  );

  const { mutate: downloadTemplate } = useDownloadGrantIdTemplate();
  const { mutate: parseUpload, isLoading: parsingUpload } =
    useParseGrantIdUpdateUpload();

  const handleDownloadTemplate = () => {
    downloadTemplate(
      selectedGrantSeeds.map((seed) => seed.grantIdentifier),
      {
        onSuccess: (blob) => {
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", "grant-id-bulk-update-template.xlsx");
          document.body.appendChild(link);
          link.click();
          link.parentNode?.removeChild(link);
          window.URL.revokeObjectURL(url);
        },
        onError: (error: any) => {
          toast.error(
            error?.response?.data?.errorMessage || "Failed to download template"
          );
        },
      }
    );
  };

  const handleFileDrop = async (newFile: File) => {
    setFile(newFile);
    setErrors([]);
    setParsedRows([]);
    const base64File = (await convertToBase64(newFile)) as string;
    parseUpload(base64File, {
      onSuccess: (rows) => {
        if (!rows.length) {
          setErrors(["No rows found in uploaded file"]);
          return;
        }
        setParsedRows(rows);
      },
      onError: (error: any) => {
        const message =
          error?.response?.data?.errorMessage ||
          error?.response?.data?.errorRaw ||
          "Failed to parse upload file";
        setErrors(Array.isArray(message) ? message : [message]);
      },
    });
  };

  const handlePreview = () => {
    if (entryMode === "upload") {
      setPreviewRows(parsedRows);
    } else {
      setPreviewRows(
        manualRows.map((row, index) => ({ ...row, rowIndex: index }))
      );
    }
    setShowPreview(true);
  };

  const updateManualRow = (
    index: number,
    key: keyof GrantIdUpdateRow,
    value: string
  ) => {
    setManualRows((rows) =>
      rows.map((row, i) => (i === index ? { ...row, [key]: value } : row))
    );
  };

  const addManualRow = () => {
    setManualRows((rows) => [
      ...rows,
      { oldGrantId: "", newGrantId: "", rowIndex: rows.length },
    ]);
  };

  const removeManualRow = (index: number) => {
    setManualRows((rows) => {
      const nextRows = rows.filter((_, i) => i !== index);
      return nextRows.length
        ? nextRows.map((row, rowIndex) => ({ ...row, rowIndex }))
        : [{ oldGrantId: "", newGrantId: "", rowIndex: 0 }];
    });
  };

  if (showPreview) {
    return (
      <BulkUpdateGrantIdsPreview
        rows={previewRows}
        selectedGrantSeeds={selectedGrantSeeds}
        onCancel={() => {
          setShowPreview(false);
          setPreviewRows([]);
        }}
      />
    );
  }

  return (
    <div className="w-full h-full bg-white rounded-lg shadow-sm border border-gray-200 flex flex-col min-h-[600px]">
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
        <div>
          <h2 className="text-lg font-semibold text-gray-800">
            Update Grant IDs
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Update system-generated grant identifiers in bulk via upload or
            manual entry.
          </p>
        </div>
        <ButtonPrimary
          className="flex items-center gap-2"
          onClick={handleDownloadTemplate}
        >
          <Icon icon="mdi:download-outline" height={18} />
          Download Template
        </ButtonPrimary>
      </div>

      <div className="px-6 py-4 border-b border-gray-100">
        <div className="inline-flex rounded-md border border-primary overflow-hidden">
          <button
            type="button"
            className={`px-4 py-2 text-sm font-medium ${
              entryMode === "upload"
                ? "bg-primary text-white"
                : "bg-white text-primary"
            }`}
            onClick={() => setEntryMode("upload")}
          >
            File Upload
          </button>
          <button
            type="button"
            className={`px-4 py-2 text-sm font-medium ${
              entryMode === "manual"
                ? "bg-primary text-white"
                : "bg-white text-primary"
            }`}
            onClick={() => setEntryMode("manual")}
          >
            Manual Input
          </button>
        </div>
        {selectedGrantSeeds.length > 0 && (
          <p className="text-xs text-gray-500 mt-3">
            {selectedGrantSeeds.length} selected grant(s) detected and prefilled
            in template/manual rows.
          </p>
        )}
      </div>

      <div className="flex-1 flex flex-col items-center justify-center p-8">
        <div className="w-full max-w-2xl space-y-8">
          {entryMode === "upload" ? (
            <>
              <div className="text-center space-y-2">
                <h3 className="text-xl font-medium text-gray-900">
                  Upload File
                </h3>
                <p className="text-gray-500">
                  Please ensure your file follows the template format before
                  uploading.
                </p>
              </div>

              <div className="w-full">
                <FileDropzone
                  onFileSelect={handleFileDrop}
                  selectedFile={file}
                  acceptedFileExtensions={[".csv", ".xlsx", ".xls"]}
                  instructionText="CSV or Excel files (.csv, .xlsx, .xls)"
                  onClear={() => {
                    setFile(undefined);
                    setErrors([]);
                    setParsedRows([]);
                  }}
                />

                {parsingUpload && (
                  <div className="mt-4 flex items-center gap-2 text-sm text-gray-500">
                    <Icon icon="eos-icons:loading" className="animate-spin" />
                    <span>Validating file...</span>
                  </div>
                )}

                {errors.length > 0 && (
                  <div className="mt-6 p-4 border border-red-200 bg-red-50 rounded-md">
                    <div className="flex items-center gap-2 mb-2 text-red-700">
                      <Icon icon="material-symbols:error-outline" height={20} />
                      <span className="font-semibold">
                        {errors.length} Error{errors.length > 1 ? "s" : ""}{" "}
                        Found
                      </span>
                    </div>
                    <ul className="list-disc list-inside space-y-1 text-sm text-red-600 max-h-40 overflow-y-auto pl-2">
                      {errors.map((err, index) => (
                        <li key={index}>{err}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              <Instructions
                data={[
                  "Download the template first to ensure the correct column format",
                  "Columns required: Old Grant Id, New Grant Id",
                  "Both columns must be filled for every row",
                  "Operation is atomic: if one row fails, no IDs are updated",
                ]}
              />

              {parsedRows.length > 0 && (
                <div className="flex justify-center pt-4">
                  <ButtonPrimary
                    className="h-10 text-base shadow-md hover:shadow-lg transition-shadow"
                    onClick={handlePreview}
                  >
                    Preview Changes
                  </ButtonPrimary>
                </div>
              )}
            </>
          ) : (
            <>
              <div className="text-center space-y-2">
                <h3 className="text-xl font-medium text-gray-900">
                  Manual Entry
                </h3>
                <p className="text-gray-500">
                  Enter old and new grant IDs for each grant you want to update.
                </p>
              </div>

              <div className="w-full">
                <table className="w-full border border-gray-200 rounded-md">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left text-xs font-semibold px-3 py-2 border-b">
                        Old Grant Id
                      </th>
                      <th className="text-left text-xs font-semibold px-3 py-2 border-b">
                        New Grant Id
                      </th>
                      <th className="text-left text-xs font-semibold px-3 py-2 border-b w-20">
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {manualRows.map((row, index) => (
                      <tr key={`manual-row-${index}`}>
                        <td className="px-3 py-2 border-b">
                          <SearchableSelect
                            options={grantIdentifiers}
                            placeholder="Select grant ID..."
                            value={row.oldGrantId || ""}
                            onChange={(e) =>
                              updateManualRow(
                                index,
                                "oldGrantId",
                                e.target.value
                              )
                            }
                          />
                        </td>
                        <td className="px-3 py-2 border-b">
                          <Input
                            value={row.newGrantId || ""}
                            onChange={(e) =>
                              updateManualRow(
                                index,
                                "newGrantId",
                                e.target.value
                              )
                            }
                            className="w-full h-9 border rounded px-2 text-sm"
                            placeholder="e.g. CUST-EMP-001"
                          />
                        </td>
                        <td className="px-3 py-2 border-b">
                          <button
                            type="button"
                            className="text-red-500 text-sm"
                            onClick={() => removeManualRow(index)}
                          >
                            Remove
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="mt-3">
                  <button
                    type="button"
                    className="text-sm text-primary"
                    onClick={addManualRow}
                  >
                    + Add Row
                  </button>
                </div>
              </div>

              <Instructions
                data={[
                  "Select the existing grant ID from the dropdown in the Old Grant Id column",
                  "Enter the new custom identifier in the New Grant Id column",
                  "New Grant Id must be unique — no duplicates within the same submission",
                  "Operation is atomic: if one row fails, no IDs are updated",
                ]}
              />

              <div className="flex justify-center pt-4">
                <ButtonPrimary
                  className="h-10 text-base shadow-md hover:shadow-lg transition-shadow"
                  onClick={handlePreview}
                >
                  Preview Changes
                </ButtonPrimary>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default BulkUpdateGrantIds;
